/**
 * 통합 API 클라이언트
 * 두 서버 통합 후 단일 엔드포인트를 제공하는 API 클라이언트
 */

import { config } from '../config';

const API_BASE_URL = config.apiBaseUrl;

// 401 오류 시 자동 로그아웃 처리 함수
const handle401Error = () => {
  console.error('🚨 401 인증 오류 감지 - 세션 만료로 자동 로그아웃 처리');

  // 1. localStorage에서 JWT 토큰 제거
  localStorage.removeItem('adminToken');

  // 2. Zustand persist 스토리지 제거 (로그인 상태 초기화)
  localStorage.removeItem('bus-auth-storage');

  // 3. 세션 만료 플래그 설정 (로그인 페이지에서 메시지 표시용)
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('session_expired', 'true');
  }

  // 4. 사용자에게 알림
  if (typeof window !== 'undefined') {
    console.warn('⚠️ 세션이 만료되었습니다. 다시 로그인해주세요.');

    // 5. 페이지 새로고침 (LoginApp에서 isAuthenticated가 false가 되어 로그인 페이지로 이동)
    window.location.reload();
  }
};

// 토큰 관리
const getAuthToken = (): string | null => {
  return localStorage.getItem('adminToken');
};

const getApiKey = (): string => {
  return 'test-api-key-12345'; // 기본 API 키
};

// HTTP 요청 래퍼
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}, 
    useApiKey = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {})
    };

    if (useApiKey) {
      // 단말기 API용 API 키 인증
      headers['X-API-Key'] = getApiKey();
    } else {
      // 웹 관리자용 JWT 인증
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const requestOptions: RequestInit = {
      ...options,
      headers
    };

    try {
      const response = await fetch(url, requestOptions);
      
      if (!response.ok) {
        // 401 인증 오류 시 자동 로그아웃 처리
        if (response.status === 401) {
          handle401Error();
          throw new Error('인증이 필요합니다.');
        }
        
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch {
          // JSON 파싱 실패 시 기본 메시지 사용
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
      
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('네트워크 연결 오류: 서버에 연결할 수 없습니다.');
      }
      throw error;
    }
  }

  // GET 요청
  async get<T>(endpoint: string, useApiKey = false): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }, useApiKey);
  }

  // POST 요청
  async post<T>(endpoint: string, data?: any, useApiKey = false): Promise<T> {
    return this.request<T>(
      endpoint, 
      {
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined
      }, 
      useApiKey
    );
  }

  // PUT 요청
  async put<T>(endpoint: string, data?: any, useApiKey = false): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined
      },
      useApiKey
    );
  }

  // DELETE 요청
  async delete<T>(endpoint: string, useApiKey = false): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, useApiKey);
  }

  // 파일 업로드
  async upload<T>(endpoint: string, formData: FormData): Promise<T> {
    const token = getAuthToken();
    const headers: Record<string, string> = {};

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData
    });

    if (!response.ok) {
      // 401 인증 오류 시 자동 로그아웃 처리
      if (response.status === 401) {
        handle401Error();
        throw new Error('인증이 필요합니다.');
      }

      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch {
        // JSON 파싱 실패 시 기본 메시지 사용
      }

      throw new Error(errorMessage);
    }

    return response.json();
  }
}

// API 클라이언트 인스턴스
const apiClient = new ApiClient(API_BASE_URL);

// 설정 내보내기
export { config };

// ApiClient 인스턴스 내보내기
export { apiClient };

// 통합 API 함수들
export const unifiedApi = {
  // 인증
  auth: {
    login: (username: string, password: string) =>
      apiClient.post('/api/auth/login', { username, password }),
    
    logout: () =>
      apiClient.post('/api/auth/logout'),
      
    refreshToken: () =>
      apiClient.post('/api/auth/refresh')
  },

  // 회사 관리
  companies: {
    list: () => apiClient.get('/api/companies'),
    create: (data: any) => apiClient.post('/api/companies', data),
    update: (id: number, data: any) => apiClient.put(`/api/companies/${id}`, data),
    delete: (id: number) => apiClient.delete(`/api/companies/${id}`)
  },

  // 노선 관리
  routes: {
    list: () => apiClient.get('/api/company-routes'),
    listByCompany: (companyId: number) => apiClient.get(`/api/company-routes?company_id=${companyId}`),
    create: (data: any) => apiClient.post('/api/company-routes', data),
    update: (id: number, data: any) => apiClient.put(`/api/company-routes/${id}`, data),
    delete: (id: number) => apiClient.delete(`/api/company-routes/${id}`),
    sync: (id: number) => apiClient.post(`/api/company-routes/${id}/sync`)
  },

  // 미디어 관리
  media: {
    list: (companyId?: number) => {
      const params = companyId ? `?company_id=${companyId}` : '';
      return apiClient.get(`/api/media/list${params}`);
    },
    upload: (formData: FormData) => apiClient.upload('/api/media/upload', formData),
    delete: (id: number) => apiClient.delete(`/api/media/${id}`),
    update: (id: number, data: any) => apiClient.put(`/api/media/${id}`, data)
  },

  // 플레이리스트 관리
  playlists: {
    list: () => apiClient.get('/api/media/playlists/list'),
    create: (data: any) => apiClient.post('/api/media/playlists', data),
    update: (id: number, data: any) => apiClient.put(`/api/media/playlists/${id}`, data),
    delete: (id: number) => apiClient.delete(`/api/media/playlists/${id}`),
    addMedia: (playlistId: number, mediaId: number, order: number) =>
      apiClient.post(`/api/media/playlists/${playlistId}/media`, { mediaId, order }),
    removeMedia: (playlistId: number, mediaId: number) =>
      apiClient.delete(`/api/media/playlists/${playlistId}/media/${mediaId}`)
  },

  // 노선별 플레이리스트
  routePlaylists: {
    list: () => apiClient.get('/api/route-playlists'),
    create: (data: any) => apiClient.post('/api/route-playlists', data),
    update: (id: number, data: any) => apiClient.put(`/api/route-playlists/${id}`, data),
    delete: (id: number) => apiClient.delete(`/api/route-playlists/${id}`)
  },

  // 단말기 관리
  devices: {
    list: (params?: Record<string, any>) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiClient.get(`/api/devices${queryString}`);
    },
    create: (data: any) => apiClient.post('/api/devices', data),
    update: (id: number, data: any) => apiClient.put(`/api/devices/${id}`, data),
    delete: (id: number) => apiClient.delete(`/api/devices/${id}`)
  },

  // 단말기 등록 관리
  deviceRegistration: {
    list: (params?: { company_code?: string }) => {
      const queryString = params && params.company_code ? `?company_code=${params.company_code}` : '';
      return apiClient.get(`/api/device-registration/registered-devices${queryString}`);
    },
    update: (id: number, data: { route_number: string }) => 
      apiClient.put(`/api/device-registration/devices/${id}`, data),
    delete: (id: number) => 
      apiClient.delete(`/api/device-registration/devices/${id}`),
    healthCheck: () => 
      apiClient.post('/api/device-registration/health-check')
  },

  // 대시보드
  dashboard: {
    stats: () => apiClient.get('/api/dashboard/stats'),
    recentActivity: () => apiClient.get('/api/dashboard/recent-activity')
  },

  // 날씨 정보
  weather: {
    current: (lat?: number, lon?: number) => {
      const params = lat && lon ? `?lat=${lat}&lon=${lon}` : '';
      return apiClient.get(`/api/weather${params}`);
    },
    summary: () => apiClient.get('/api/weather/summary'),
    routeWeather: (routeId: number, limit: number = 24) => 
      apiClient.get(`/api/weather/route/${routeId}?limit=${limit}`),
    update: () => apiClient.post('/api/weather/update'),
    schedulerStatus: () => apiClient.get('/api/weather/scheduler/status'),
    testApiKey: (apiKey: string) => apiClient.post('/api/weather/test-api-key', { apiKey })
  },

  // 앱 설정 관리
  appConfig: {
    get: () => apiClient.get('/api/app-config'),
    update: (config: any) => apiClient.put('/api/app-config', { config })
  },

  // 뉴스 RSS 관리 API
  news: {
    list: (params?: { company_id?: number }) => {
      const queryString = params ? '?' + new URLSearchParams(
        Object.entries(params).reduce((acc, [key, value]) => {
          if (value !== undefined) acc[key] = value.toString();
          return acc;
        }, {} as Record<string, string>)
      ).toString() : '';
      return apiClient.get(`/api/news${queryString}`);
    },
    create: (data: {
      company_id: number;
      route_id?: number;
      source_name: string;
      rss_url: string;
      category?: string;
    }) => apiClient.post('/api/news', data),
    update: (id: number, data: {
      route_id?: number;
      source_name?: string;
      rss_url?: string;
      category?: string;
      is_active?: boolean;
    }) => apiClient.put(`/api/news/${id}`, data),
    delete: (id: number) => apiClient.delete(`/api/news/${id}`),
    testRss: (data: { rss_url: string }) => apiClient.post('/api/news/test-rss', data)
  },

  // 타겟 광고 관리
  targetedAds: {
    list: (params?: Record<string, any>) => {
      const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
      return apiClient.get(`/api/targeted-ads${queryString}`);
    },
    create: (data: any) => apiClient.post('/api/targeted-ads', data),
    update: (id: number, data: any) => apiClient.put(`/api/targeted-ads/${id}`, data),
    delete: (id: number) => apiClient.delete(`/api/targeted-ads/${id}`)
  },

  // 단말기 API (API 키 인증 필요)
  device: {
    config: () => apiClient.get('/api/app-config/device', true),
    updateConfig: (config: any) => apiClient.post('/api/app-config/device', config, true),
    version: () => apiClient.get('/api/device/version', true),
    media: (filename: string) => apiClient.get(`/api/device/media/${filename}`, true),
    mediaMeta: (filename: string) => apiClient.get(`/api/device/media/${filename}/meta`, true),
    weather: (lat?: number, lon?: number) => {
      const params = lat && lon ? `?lat=${lat}&lon=${lon}` : '';
      return apiClient.get(`/api/device/weather${params}`, true);
    },
    busInfo: (routeNumber: string) => 
      apiClient.get(`/api/device/bus-info?route_number=${routeNumber}`, true),
    generalAds: () => apiClient.get('/api/device/ads/general', true),
    targetedAds: () => apiClient.get('/api/device/ads/targeted', true),
    busStops: (params?: { route_id?: number; lat?: number; lon?: number; radius?: number }) => {
      const queryString = params ? '?' + new URLSearchParams(
        Object.entries(params).reduce((acc, [key, value]) => {
          if (value !== undefined) acc[key] = value.toString();
          return acc;
        }, {} as Record<string, string>)
      ).toString() : '';
      return apiClient.get(`/api/device/busstops${queryString}`, true);
    },
    routePlaylist: (routeNumber: string) => 
      apiClient.get(`/api/device/route/${routeNumber}/playlist`, true),
    news: (routeNumber: string, companyCode?: string) => {
      const params = companyCode ? `?route_number=${routeNumber}&company_code=${companyCode}` 
                                  : `?route_number=${routeNumber}`;
      return apiClient.get(`/api/news/device/news${params}`, true);
    }
  }
};

export default unifiedApi;